import {ModalForm, ProFormTreeSelect} from '@ant-design/pro-components';
import * as React from 'react';
import {useState, useEffect, useMemo} from 'react';
import {Typography, TreeSelect, Tag, notification} from "antd";
import axiosApiInstance from "../../../api/axiosClient";
import {useAtomValue} from "jotai";
import {queryClientAtom} from "jotai-tanstack-query";
import {generate} from '@ant-design/colors';
import {getPaletteColor} from "./utils";

type Props = {
    creatorsIds: number[]
    onClearSelection?: any
};

export type TagCategoryValue = {
    label: string;
    value: string;
    checked: boolean;
    color: string;
};

export type TagCategory = {
    label: string;
    value: string;
    checkable: boolean;
    allowMultiple: boolean;
    children: TagCategoryValue[];
};

export const AddBulkCreatorTags = (props: Props) => {
    const queryClient = useAtomValue(queryClientAtom);
    const [tagOptions, setTagOptions] = useState<TagCategory[]>([]);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const {SHOW_PARENT} = TreeSelect;

    // Fetch tag options when component mounts
    useEffect(() => {
        const fetchTagOptions = async () => {
            try {
                const response = await axiosApiInstance.get('/api/creators/tags/');
                setTagOptions(response.data);
            } catch (error) {
                console.error('Error fetching tag options:', error);
            }
        };
        fetchTagOptions();
    }, []);

    // Transform and sort tag options
    const transformedTagOptions = useMemo(() => {
        if (!tagOptions) return [];
        
        const transformed = tagOptions.map(option => ({
            ...option,
            label: option.label === 'Specific' ? 'Content Category' : option.label
        }));
        
        const sorted = [...transformed].sort((a, b) => {
            const priorityCategories = ['Prime Channel Category', 'Content Category', 'Channel Type', 'Gender'];
            const aIndex = priorityCategories.indexOf(a.label);
            const bIndex = priorityCategories.indexOf(b.label);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return 0;
        });
        
        return sorted;
    }, [tagOptions]);

    const handleChange = (values: string[]) => {
        setSelectedValues(values);
    };

    const tagRender = (props) => {
        const {label, value, closable, onClose} = props;
        const [categoryId, valueId] = (value as string).split(',').map(Number);
        
        const category = tagOptions.find(cat => cat.value === String(categoryId));
        const tagValue = category?.children?.find(val => val.value === `${categoryId},${valueId}`);
        
        if (!category || !tagValue) return null;
        
        const primaryColor = tagValue.color;
        const palette = generate(primaryColor);
        const appropriateColor = getPaletteColor(primaryColor, palette);
        
        return (
            <Tag
                color={primaryColor}
                closable={closable}
                onClose={onClose}
                style={{
                    marginRight: 3,
                    color: appropriateColor
                }}
            >
                {label}
            </Tag>
        );
    };

    return (
        <ModalForm
            width={600}
            title={`Add Tags to ${props.creatorsIds.length} Creator${props.creatorsIds.length > 1 ? 's' : ''}`}
            trigger={
                <Typography.Link>
                    Add tag
                </Typography.Link>
            }
            autoFocusFirstInput
            modalProps={{
                destroyOnClose: true,
                okText: 'Add Tags',
                cancelText: 'Cancel'
            }}
            onFinish={async (values) => {
                setLoading(true);
                try {
                    // Transform selected values to the format the backend expects
                    const tags = selectedValues.map(val => {
                        const [categoryId, valueId] = val.split(',').map(Number);
                        return {
                            tag_category_id: categoryId,
                            tag_category_value_id: valueId
                        };
                    });

                    await axiosApiInstance.post('/api/creators/items/tags/bulk', {
                        creator_ids: props.creatorsIds,
                        tags: tags
                    });

                    notification.success({
                        message: 'Success',
                        description: `Tags added to ${props.creatorsIds.length} creator${props.creatorsIds.length > 1 ? 's' : ''}`
                    });

                    queryClient.invalidateQueries({queryKey: ['creators']});
                    if (props.onClearSelection) {
                        props.onClearSelection();
                    }
                    setSelectedValues([]);
                    return true;
                } catch (error) {
                    console.error('Error adding tags:', error);
                    notification.error({
                        message: 'Error',
                        description: 'Failed to add tags. Please try again.'
                    });
                    return false;
                } finally {
                    setLoading(false);
                }
            }}
        >
            <ProFormTreeSelect
                width="xl"
                label="Select Tags to Add"
                name="tags"
                fieldProps={{
                    showCheckedStrategy: SHOW_PARENT,
                    multiple: true,
                    tagRender: tagRender,
                    treeCheckable: true,
                    treeExpandAction: "click",
                    value: selectedValues,
                    onChange: handleChange,
                    treeData: transformedTagOptions,
                    placeholder: "Select tags to add to all selected creators",
                    maxTagCount: 'responsive',
                    filterTreeNode: (inputValue: string, treeNode: any) =>
                        String(treeNode.label ?? treeNode.title ?? '').toLowerCase().includes(inputValue.toLowerCase()),
                }}
            />
        </ModalForm>
    );
};

export default AddBulkCreatorTags;
